import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setUpdatedVTPLeadForm, setUpdatedPromotionsLeadForm } from './useIxisEvents';
import { digitalLeadsFormLoad } from '../utils/analyticsEventListeners';
import { FEATURE_APP_DETAILS } from '../constants';
import { StockCarTypes, Vehicle, VehicleDataType } from '../types';
import { setForm } from '../redux/formReducer';

interface Props {
  isModalOpen: boolean;
  vehicle: any;
  vehicleInView: any;
  offers: any;
  dealerId: string | null;
  leadSource: string | undefined;
  contactForm: any;
  promotion?: any;
  isAudiMake: boolean;
  excludeYearFromModelName: (isAudi: boolean) => string | undefined;
}

export default function useIxisFormTracking({
  isModalOpen,
  vehicle,
  vehicleInView,
  offers,
  dealerId,
  leadSource,
  contactForm,
  isAudiMake,
  excludeYearFromModelName,
}: Props) {
  const dispatch = useDispatch();

  const [vehicleStatus, setVehicleStatus] = useState<string>();
  const [vehicleSaleType, setVehicleSaleType] = useState<string>();
  const [vehicleSaleOrderType, setVehicleSaleOrderType] = useState<string | null>();
  const [orderStatusVehicle, setOrderStatusVehicle] = useState<string | null>();
  const [vehicleWithEditedNames, setVehicleWithEditedNames] = useState<StockCarTypes>();
  const [vehicleDataForPromotions, setVehicleDataForPromotions] = useState<Vehicle>();

  const promotionFromOffers = useMemo(
    () => (offers ? { ...convertOffersToPromotion(offers) } : undefined),
    [offers],
  );

  function convertOffersToPromotion(o: any) {
    return o;
  }

  const prevComputedRef = useRef({
    status: undefined as string | undefined,
    saleType: undefined as string | undefined,
    orderType: null as string | null | undefined,
    orderStatus: null as string | null | undefined,
    vtpFormKey: '' as string, // simple hash of VTP inputs to guard dispatch
    promoFormKey: '' as string, // simple hash of promo inputs to guard dispatch
  });

  useEffect(() => {
    if (!isModalOpen) return;

    let status: string | undefined;
    let saleType: string | undefined;
    let orderType: string | undefined | null = null;
    let orderStatus: string | null = null;

    const vehicleInventoryType = vehicleInView?.salesInfo?.availableFromDateInfo?.value;
    const isAvailableNow = vehicleInventoryType === 'Immediately';
    const vehicleOrderStatus = vehicleInView?.salesInfo?.orderStatusText;
    const saleOrderType = vehicleInView?.salesInfo?.saleOrderTypeText;

    if (vehicleInView) {
      status =
        vehicleInView.cartypeText === 'U' && isAvailableNow
          ? 'available'
          : vehicleOrderStatus || 'Unavailable';

      saleType =
        vehicleInView.cartypeText === 'U' && isAvailableNow
          ? 'available-now'
          : saleOrderType || vehicleInventoryType;

      if (!isAvailableNow) {
        orderType = saleOrderType;
        orderStatus = vehicleOrderStatus ?? null;
      }
    }

    const prev = prevComputedRef.current;
    if (prev.status !== status) {
      setVehicleStatus(status);
      prev.status = status;
    }
    if (prev.saleType !== saleType) {
      setVehicleSaleType(saleType);
      prev.saleType = saleType;
    }
    if (prev.orderType !== orderType) {
      setVehicleSaleOrderType(orderType ?? null);
      prev.orderType = orderType;
    }
    if (prev.orderStatus !== orderStatus) {
      setOrderStatusVehicle(orderStatus ?? null);
      prev.orderStatus = orderStatus;
    }

    // ---- VTP Lead case (no offers) ----
    const isVtpLead = !!vehicle && leadSource !== 'special_offers' && leadSource !== 'offers';
    if (isVtpLead) {
      const trimmedName = excludeYearFromModelName(isAudiMake);
      const vehicleTrimmed: StockCarTypes = {
        ...vehicle,
        titleText: trimmedName || '',
        salesInfo: {
          ...vehicle.salesInfo,
          saleOrderTypeText: orderType ?? undefined,
          orderStatusText: orderStatus ?? undefined,
        },
      };

      setVehicleWithEditedNames((prevV) => {
        const changed =
          !prevV ||
          prevV.titleText !== vehicleTrimmed.titleText ||
          prevV.salesInfo.saleOrderTypeText !== vehicleTrimmed.salesInfo.saleOrderTypeText ||
          prevV.salesInfo.orderStatusText !== vehicleTrimmed.salesInfo.orderStatusText;
        return changed ? vehicleTrimmed : prevV;
      });

      const updatedForm = setUpdatedVTPLeadForm(contactForm, vehicleTrimmed);

      const vtpKey = JSON.stringify({
        vehicleModelName: vehicleTrimmed.titleText,
        orderType,
        orderStatus,
        form: updatedForm?.formData,
      });

      if (prev.vtpFormKey !== vtpKey) {
        dispatch(setForm(updatedForm));
        digitalLeadsFormLoad(updatedForm, vehicleTrimmed, FEATURE_APP_DETAILS);
        prev.vtpFormKey = vtpKey;
      }
    }

    // ---- Promotions Lead case (offers) ----
    const isPromoLead = !!offers && (leadSource === 'special_offers' || leadSource === 'offers');
    if (isPromoLead) {
      const { modelName, modelYear, trimName } = offers || {};
      const vehicleData = {
        modelName,
        modelYear,
        trimName,
      } as Vehicle;

      setVehicleDataForPromotions((prevV) => {
        const changed =
          !prevV ||
          prevV.modelName !== vehicleData.modelName ||
          prevV.modelYear !== vehicleData.modelYear ||
          prevV.trimName !== vehicleData.trimName;
        return changed ? vehicleData : prevV;
      });

      const updatedForm = setUpdatedPromotionsLeadForm(contactForm, dealerId!, modelName);

      const promoKey = JSON.stringify({
        dealerId,
        modelName,
        form: updatedForm?.formData,
      });

      if (prev.promoFormKey !== promoKey) {
        dispatch(setForm(updatedForm));
        digitalLeadsFormLoad(updatedForm, vehicleData, FEATURE_APP_DETAILS, promotionFromOffers);
        prev.promoFormKey = promoKey;
      }
    }
  }, [
    isModalOpen,
    vehicleInView,
    vehicle,
    offers,
    dealerId,
    leadSource,
    isAudiMake,
    excludeYearFromModelName,
    contactForm,
    dispatch,
    promotionFromOffers,
  ]);

  return {
    vehicleStatus,
    vehicleSaleType,
    vehicleSaleOrderType,
    orderStatusVehicle,
    vehicleWithEditedNames,
    vehicleDataForPromotions,
  };
}
